/**
 * useExperienceState — single source of truth for an Experience's
 * emotional/operational phase (VÉRAULT Member Suite · Wave 26.21 Phase 2a).
 *
 * Five states drive Overview behavior — the order of the SmartStatusStrip,
 * the tone of the ExperienceFlowPreview, and (Phase 2b+) reminder cadence:
 *
 *   ▸ planning    — no firm dates yet, or > 60 days out
 *   ▸ confirmed   — dates set, ≤ 60 days, > 14 days out
 *   ▸ preparing   — ≤ 14 days out, not yet started
 *   ▸ traveling   — today is between start and end
 *   ▸ completed   — end date has passed
 *
 * Derivation is pure date math against `trip_start_date` / `trip_end_date`
 * (or the legacy `start_date` / `end_date` fallbacks). No backend call.
 */

const DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Derive an experience's emotional/operational state.
 *
 * Wave 30 · Phase 7d — accepts an optional IANA timezone so "today" is
 * computed in the traveler's local frame, not the server's UTC clock.
 * Backwards-compatible: when no timezone is supplied (or `trip.destination_timezone`
 * is absent), falls back to UTC noon math.
 */
function _localISODate(timezone, when) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(when);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    /* fall through */
  }
  return when.toISOString().slice(0, 10);
}

export function deriveExperienceState(trip, now = new Date()) {
  if (!trip) return { state: "planning", daysToStart: null, daysToEnd: null };
  const startStr = trip.trip_start_date || trip.start_date;
  const endStr = trip.trip_end_date || trip.end_date;
  if (!startStr) return { state: "planning", daysToStart: null, daysToEnd: null };

  const timezone =
    trip.destination_timezone || trip.default_timezone || trip.reveal_timezone || trip.timezone || null;

  // Use noon to avoid DST drift on simple date strings.
  const start = new Date(`${startStr.slice(0, 10)}T12:00:00Z`);
  const end = endStr ? new Date(`${endStr.slice(0, 10)}T12:00:00Z`) : start;

  // If a trip TZ is available, compute "today" in the traveler's local
  // frame; otherwise fall back to UTC.
  const todayISO = timezone ? _localISODate(timezone, now) : now.toISOString().slice(0, 10);
  const today = new Date(`${todayISO}T12:00:00Z`);

  const daysToStart = Math.round((start - today) / DAY_MS);
  const daysToEnd = Math.round((end - today) / DAY_MS);

  let state;
  if (daysToEnd < 0) state = "completed";
  else if (daysToStart <= 0 && daysToEnd >= 0) state = "traveling";
  else if (daysToStart <= 14) state = "preparing";
  else if (daysToStart <= 60) state = "confirmed";
  else state = "planning";

  // Wave 36 Phase 1 P2 — explicit lifecycle_status from the advisor wins
  // over the date-derived state for the post-trip stages. This lets the
  // advisor open a "Welcome Home" surface even before/after the end_date.
  const lifecycle = trip.lifecycle_status;
  if (lifecycle === "welcome_home") state = "welcome_home";
  else if (lifecycle === "memories") state = "memories";

  return { state, daysToStart, daysToEnd, timezone };
}

const STATE_LABELS = {
  planning: { label: "Curating", tone: "muted", hint: "Quietly composing the shape of this experience." },
  confirmed: { label: "Confirmed", tone: "gold", hint: "Held in place. We'll narrow the details as it nears." },
  preparing: { label: "Preparing", tone: "gold", hint: "Final touches — packing soon." },
  traveling: { label: "Traveling", tone: "sage", hint: "You're inside the experience." },
  completed: { label: "Completed", tone: "muted", hint: "A chapter closed. Memories below." },
  welcome_home: { label: "Welcome Home", tone: "gold", hint: "The journey came home with you." },
  memories: { label: "Memories", tone: "muted", hint: "Quietly archived — yours to revisit." },
};

export function getStateLabel(state) {
  return STATE_LABELS[state] || STATE_LABELS.planning;
}

export default deriveExperienceState;
